import React, { useState } from 'react';
import {
  X, Mail, Phone, Users, Briefcase, MapPin, User, Calendar, Clock,
  CheckCircle, BookOpen, Target, Download, Shield, Key, Settings, Info
} from 'lucide-react';
import './UserDetailModal.css';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  jobTitle: string;
  department: string;
  initials: string;
  progress: number;
  phone?: string;
  location?: string;
  manager?: string;
  joinDate: string;
  lastLogin: string;
  completedCourses: number;
  inProgressCourses: number;
}

interface Course {
  id: string;
  name: string;
  duration: string;
  lessons: number;
}

interface LearningHistoryItem {
  id: string;
  name: string;
  type: string;
  status: string;
  progress: number;
  score: string;
  enrolled: string;
  completed: string;
  certificate: boolean;
}

interface trainerActivity {
  action: string;
  date: string;
}

interface UserDetailModalProps {
  user: TeamMember;
  courses: Course[];
  learningHistory: LearningHistoryItem[];
  trainerActivities: trainerActivity[];
  onClose: () => void;
  onEnrollCourse: (courseName: string) => void;
}

const UserDetailModal: React.FC<UserDetailModalProps> = ({
  user,
  courses,
  learningHistory,
  trainerActivities,
  onClose,
  onEnrollCourse
}) => {
  const [activeTab, setActiveTab] = useState<'details' | 'history' | 'enrollment' | 'trainer'>('details');

  return (
    <div className="trainer-user-modal-overlay">
      <div className="trainer-user-detail-modal">
        <div className="trainer-user-detail-header">
          <div className="trainer-user-detail-info">
            <div className="trainer-user-avatar trainer-user-avatar-large">{user.initials}</div>
            <div>
              <h2 className="trainer-user-detail-name">{user.name}</h2>
              <p className="trainer-user-detail-title">{user.jobTitle}</p>
            </div>
            <span className="trainer-user-status-badge-active">Active</span>
          </div>
          <button className="trainer-user-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="trainer-user-detail-tabs">
          <button
            className={`trainer-user-detail-tab ${activeTab === 'details' ? 'trainer-user-detail-tab-active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            User Details
          </button>
          <button
            className={`trainer-user-detail-tab ${activeTab === 'history' ? 'trainer-user-detail-tab-active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            Learning History
          </button>
          <button
            className={`trainer-user-detail-tab ${activeTab === 'enrollment' ? 'trainer-user-detail-tab-active' : ''}`}
            onClick={() => setActiveTab('enrollment')}
          >
            Manual Enrollment
          </button>
          <button
            className={`trainer-user-detail-tab ${activeTab === 'trainer' ? 'trainer-user-detail-tab-active' : ''}`}
            onClick={() => setActiveTab('trainer')}
          >
            trainer Actions
          </button>
        </div>

        <div className="trainer-user-detail-content">
          {activeTab === 'details' && (
            <div className="trainer-user-details-tab">
              <section className="trainer-user-detail-section">
                <h3 className="trainer-user-detail-section-title">Personal Information</h3>
                <div className="trainer-user-info-grid">
                  <div className="trainer-user-info-item">
                    <div className="trainer-user-info-label">EMAIL</div>
                    <div className="trainer-user-info-value">
                      <Mail size={16} /> {user.email}
                    </div>
                  </div>
                  <div className="trainer-user-info-item">
                    <div className="trainer-user-info-label">PHONE</div>
                    <div className="trainer-user-info-value">
                      <Phone size={16} /> {user.phone}
                    </div>
                  </div>
                  <div className="trainer-user-info-item">
                    <div className="trainer-user-info-label">DEPARTMENT</div>
                    <div className="trainer-user-info-value">
                      <Users size={16} /> {user.department}
                    </div>
                  </div>
                  <div className="trainer-user-info-item">
                    <div className="trainer-user-info-label">JOB TITLE</div>
                    <div className="trainer-user-info-value">
                      <Briefcase size={16} /> {user.jobTitle}
                    </div>
                  </div>
                  <div className="trainer-user-info-item">
                    <div className="trainer-user-info-label">LOCATION</div>
                    <div className="trainer-user-info-value">
                      <MapPin size={16} /> {user.location}
                    </div>
                  </div>
                  <div className="trainer-user-info-item">
                    <div className="trainer-user-info-label">MANAGER</div>
                    <div className="trainer-user-info-value">
                      <User size={16} /> {user.manager}
                    </div>
                  </div>
                  <div className="trainer-user-info-item">
                    <div className="trainer-user-info-label">JOIN DATE</div>
                    <div className="trainer-user-info-value">
                      <Calendar size={16} /> {user.joinDate}
                    </div>
                  </div>
                  <div className="trainer-user-info-item">
                    <div className="trainer-user-info-label">LAST LOGIN</div>
                    <div className="trainer-user-info-value">
                      <Clock size={16} /> {user.lastLogin}
                    </div>
                  </div>
                </div>
              </section>

              <section className="trainer-user-detail-section">
                <h3 className="trainer-user-detail-section-title">Learning Statistics</h3>
                <div className="trainer-user-stats-grid">
                  <div className="trainer-user-stat-box">
                    <div className="trainer-user-stat-box-label">Completed</div>
                    <div className="trainer-user-stat-box-value">{user.completedCourses}</div>
                    <div className="trainer-user-stat-box-icon">
                      <CheckCircle size={32} />
                    </div>
                  </div>
                  <div className="trainer-user-stat-box">
                    <div className="trainer-user-stat-box-label">In Progress</div>
                    <div className="trainer-user-stat-box-value">{user.inProgressCourses}</div>
                    <div className="trainer-user-stat-box-icon">
                      <BookOpen size={32} />
                    </div>
                  </div>
                  <div className="trainer-user-stat-box">
                    <div className="trainer-user-stat-box-label">Completion Rate</div>
                    <div className="trainer-user-stat-box-value">{user.progress}%</div>
                    <div className="trainer-user-stat-box-icon">
                      <Target size={32} />
                    </div>
                  </div>
                </div>
              </section>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="trainer-user-history-tab">
              <div className="trainer-user-history-tab-header">
                <h3 className="trainer-user-detail-section-title">Learning History</h3>
                <button className="trainer-user-export-btn">
                  <Download size={16} /> Export Transcript
                </button>
              </div>

              <div className="trainer-user-table-container">
                <table className="trainer-user-table">
                  <thead>
                    <tr>
                      <th>COURSE</th>
                      <th>TYPE</th>
                      <th>STATUS</th>
                      <th>PROGRESS</th>
                      <th>SCORE</th>
                      <th>COMPLETED</th>
                    </tr>
                  </thead>
                  <tbody>
                    {learningHistory.map(item => (
                      <tr key={item.id}>
                        <td>
                          <div className="trainer-user-course-info">
                            <div className="trainer-user-course-icon">
                              <BookOpen size={20} />
                            </div>
                            <span>{item.name}</span>
                          </div>
                        </td>
                        <td>{item.type}</td>
                        <td>
                          <span className={`trainer-user-status-badge ${
                            item.status === 'Completed' ? 'trainer-user-status-completed' : 'trainer-user-status-progress'
                          }`}>
                            {item.status}
                          </span>
                        </td>
                        <td>
                          <div className="trainer-user-progress-cell">
                            <span>{item.progress}%</span>
                            <div className="trainer-user-progress-bar">
                              <div
                                className="trainer-user-progress-fill"
                                style={{ width: `${item.progress}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td>{item.score}</td>
                        <td>{item.completed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'enrollment' && (
            <div className="trainer-user-enrollment-tab">
              <div className="trainer-user-enrollment-header">
                <h3 className="trainer-user-detail-section-title">Manual Enrollment</h3>
                <p className="trainer-user-enrollment-subtitle">Enroll {user.name} in courses, paths, or certifications</p>
              </div>

              <input
                type="text"
                className="trainer-user-search-input"
                placeholder="Search learning items..."
              />

              <div className="trainer-user-enrollment-list">
                {courses.map(course => (
                  <div key={course.id} className="trainer-user-enrollment-item">
                    <div className="trainer-user-enrollment-item-info">
                      <div className="trainer-user-course-icon">
                        <BookOpen size={20} />
                      </div>
                      <div>
                        <div className="trainer-user-enrollment-item-name">{course.name}</div>
                        <div className="trainer-user-enrollment-item-meta">
                          Course • {course.duration} • {course.lessons} modules
                        </div>
                      </div>
                    </div>
                    <button
                      className="trainer-user-enroll-btn"
                      onClick={() => onEnrollCourse(course.name)}
                    >
                      + Enroll
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'trainer' && (
            <div className="trainer-user-trainer-tab">
              <div className="trainer-user-trainer-header">
                <h3 className="trainer-user-detail-section-title">traineristrative Actions</h3>
                <p className="trainer-user-enrollment-subtitle">Perform traineristrative tasks for this user</p>
              </div>

              <div className="trainer-user-current-status">
                <div className="trainer-user-status-label">CURRENT STATUS</div>
                <div className="trainer-user-status-info">
                  <div className="trainer-user-status-icon">
                    <Shield size={24} />
                  </div>
                  <div>
                    <span className="trainer-user-status-badge-active">Active</span>
                    <span className="trainer-user-status-last-login"> • Last login: {user.lastLogin}</span>
                  </div>
                </div>
              </div>

              <div className="trainer-user-trainer-actions-grid">
                <div className="trainer-user-trainer-action-card">
                  <div className="trainer-user-trainer-action-icon trainer-user-icon-blue">
                    <Shield size={28} />
                  </div>
                  <h4 className="trainer-user-trainer-action-title">Update Status</h4>
                  <p className="trainer-user-trainer-action-desc">Change account status</p>
                  <button className="trainer-user-trainer-action-btn trainer-user-btn-blue">Change Status</button>
                </div>

                <div className="trainer-user-trainer-action-card">
                  <div className="trainer-user-trainer-action-icon trainer-user-icon-purple">
                    <Key size={28} />
                  </div>
                  <h4 className="trainer-user-trainer-action-title">Reset Password</h4>
                  <p className="trainer-user-trainer-action-desc">Send reset link</p>
                  <button className="trainer-user-trainer-action-btn trainer-user-btn-purple">Send Reset Link</button>
                </div>

                <div className="trainer-user-trainer-action-card">
                  <div className="trainer-user-trainer-action-icon trainer-user-icon-yellow">
                    <Settings size={28} />
                  </div>
                  <h4 className="trainer-user-trainer-action-title">Change Role</h4>
                  <p className="trainer-user-trainer-action-desc">Update permissions</p>
                  <button className="trainer-user-trainer-action-btn trainer-user-btn-yellow">Update Role</button>
                </div>
              </div>

              <div className="trainer-user-guidelines-box">
                <div className="trainer-user-guidelines-header">
                  <Info size={18} className="trainer-user-guidelines-icon" />
                  <span className="trainer-user-guidelines-title">Status Change Guidelines</span>
                </div>
                <div className="trainer-user-guidelines-content">
                  <div className="trainer-user-guideline-item">
                    <strong>Active:</strong> User has full access to the platform
                  </div>
                  <div className="trainer-user-guideline-item">
                    <strong>Inactive:</strong> Temporary access suspension (can be reactivated)
                  </div>
                  <div className="trainer-user-guideline-item">
                    <strong>Suspended:</strong> Account under review or policy violation
                  </div>
                  <div className="trainer-user-guideline-item">
                    <strong>Terminated:</strong> Employment ended, permanent deactivation
                  </div>
                  <div className="trainer-user-guideline-item">
                    <strong>Resigned:</strong> User voluntarily left the organization
                  </div>
                </div>
              </div>

              <div className="trainer-user-activity-section">
                <h4 className="trainer-user-activity-title">Recent trainer Activity</h4>
                <div className="trainer-user-activity-list">
                  {trainerActivities.map((activity, index) => (
                    <div key={index} className="trainer-user-activity-item">
                      <div className="trainer-user-activity-icon">
                        <Clock size={16} />
                      </div>
                      <div>
                        <div className="trainer-user-activity-action">{activity.action}</div>
                        <div className="trainer-user-activity-date">{activity.date}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetailModal;
